<script setup lang="ts">
import { vOnClickOutside } from "@vueuse/components"

const props = withDefaults(
  defineProps<{
    alignY?: string
    alignX?: string
    customClass?: string
    customInnerClass?: string
    customTopClass?: string
    customBodyClass?: string
    title?: string
    description?: string
    body?: boolean
    headerClass?: string
    header?: boolean
    closeOnClickOutside?: boolean
  }>(),
  {
    alignY: "center",
    alignX: "center",
    customClass: "center",
    customBodyClass: "p-5",
    body: true,
    header: true,
    closeOnClickOutside: true,
  },
)

const emit = defineEmits(["close"])

const show = defineModel<boolean>()

function handleClose() {
  show.value = false
  emit("close")
}

function handleClickOutside() {
  if (props.closeOnClickOutside) {
    handleClose()
  }
}
</script>

<template>
  <Teleport to="#__nuxt">
    <Transition name="fade" mode="out-in">
      <div
        v-show="show"
        id="defaultModal"
        tabindex="-1"
        :class="
          customTopClass || {
            'justify-start': alignX === 'start',
            'justify-end': alignX === 'end',
            'justify-center': alignX === 'center',
            'items-start': alignY === 'start',
            'items-bottom': alignY === 'bottom',
            'items-center': alignY === 'center',
          }
        "
        class="fixed inset-x-0 top-0 z-50 m-auto flex h-dvh overflow-y-auto overflow-x-hidden bg-portal-indigo-900/90 backdrop-blur backdrop-saturate-100 md:inset-0"
        aria-modal="true"
        role="dialog"
      >
        <Transition name="fade" mode="out-in">
          <div
            v-if="body"
            class="max-h-lg relative w-full max-w-xl sm:p-4"
            :class="customClass"
          >
            <div
              v-on-click-outside="handleClickOutside"
              class="relative h-dvh overflow-hidden rounded-none bg-white drop-shadow-xl sm:h-auto md:rounded-lg"
              :class="customInnerClass"
            >
              <div
                v-if="header"
                class="flex justify-between p-5"
                :class="headerClass"
              >
                <div>
                  <slot name="header" />
                  <h1 v-if="title" class="font-semibol text-2xl">
                    {{ title }}
                  </h1>
                  <div v-if="description" class="text-gray-600">
                    {{ description }}
                  </div>
                </div>

                <UButton
                  size="sm"
                  circle
                  variant="quite"
                  class="hover:text-[--theme-medium]"
                  @click="handleClose"
                >
                  <template #prepend>
                    <UIcon name="close" />
                  </template>
                </UButton>
              </div>
              <div :class="customBodyClass">
                <slot />
              </div>
            </div>
          </div>
          <div v-else v-on-click-outside="handleClickOutside">
            <slot />
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
